import { matchPath } from 'react-router-dom';

export const SITE_NAME = 'Grim Dark Companion';

/**
 * Document title per route, most specific first. `/` is intentionally absent: the public home
 * page keeps the marketing title from index.html. Print routes are absent too, because
 * RosterPrint sets its own title (it becomes the PDF filename).
 * Every `path` in App.tsx must match one of these; pageTitles.test.ts checks that.
 */
const PAGE_TITLES: { path: string; title: string }[] = [
  { path: '/login', title: 'Sign in' },
  { path: '/register', title: 'Create an account' },
  { path: '/waitlist', title: 'Join the waitlist' },
  { path: '/auth/verify-email', title: 'Verify your email' },
  { path: '/forgot-password', title: 'Forgot your password' },
  { path: '/auth/reset-password', title: 'Reset your password' },
  { path: '/auth/error', title: 'Sign-in problem' },
  { path: '/auth/callback', title: 'Signing you in' },
  { path: '/privacy-policy', title: 'Privacy Policy' },
  { path: '/terms-and-conditions', title: 'Terms and Conditions' },
  { path: '/about', title: 'About' },
  { path: '/donate', title: 'Support us' },
  { path: '/premium/success', title: 'Premium activated' },
  { path: '/premium/cancelled', title: 'Premium checkout cancelled' },
  { path: '/premium', title: 'Premium' },
  { path: '/welcome', title: 'Welcome' },
  { path: '/home', title: 'Home' },
  { path: '/battles/new', title: 'New battle' },
  { path: '/battles/log/:id', title: 'Edit result' },
  { path: '/battles/log', title: 'Log a result' },
  { path: '/battles/:id/report', title: 'Battle report' },
  { path: '/battles/:id', title: 'Battle' },
  { path: '/battles', title: 'Battles' },
  { path: '/rosters/new', title: 'New roster' },
  { path: '/rosters/:id', title: 'Roster' },
  { path: '/rosters', title: 'Rosters' },
  { path: '/collection/:gameSystemId/items/:itemId', title: 'Unit' },
  { path: '/collection/:gameSystemId', title: 'Collection' },
  { path: '/collection', title: 'Collection' },
  { path: '/near-me/venues/:id/manage', title: 'Manage venue' },
  { path: '/near-me/venues/:id', title: 'Venue' },
  { path: '/near-me/events/:id', title: 'Event' },
  { path: '/near-me/submit', title: 'Add to Near Me' },
  { path: '/near-me', title: 'Near Me' },
  { path: '/tools/:toolId', title: 'Tools' },
  { path: '/tools', title: 'Tools' },
  { path: '/hobby', title: 'Hobby' },
  { path: '/journal', title: 'Journal' },
  { path: '/projects/new', title: 'New project' },
  { path: '/projects/:id', title: 'Project' },
  { path: '/projects', title: 'Projects' },
  { path: '/community', title: 'Community' },
  { path: '/paint-recipes', title: 'Paint recipes' },
  { path: '/grimm', title: 'Grimm' },
  { path: '/help', title: 'Help' },
  { path: '/you/profile', title: 'Profile' },
  { path: '/you/friends', title: 'Friends' },
  { path: '/you/achievements', title: 'Achievements' },
  { path: '/you/stats', title: 'Stats' },
  { path: '/you/settings', title: 'Settings' },
  { path: '/you/plan', title: 'Plan & usage' },
  { path: '/settings', title: 'Settings' },
  { path: '/profile', title: 'Profile' },
  { path: '/events/:country/:area/:town', title: 'Local wargaming events' },
  { path: '/events/:country/:area', title: 'Local wargaming events' },
  { path: '/events', title: 'Local wargaming events' },
  { path: '/venues/:id/:slug', title: 'Club or store' },
  { path: '/play', title: 'Play' },
  { path: '/admin/community', title: 'Community moderation' },
  { path: '/admin/near-me/venues/:id', title: 'Venue managers' },
  { path: '/admin/near-me/seed', title: 'Seed import' },
  { path: '/admin/near-me', title: 'Near Me moderation' },
  { path: '/admin/users', title: 'Manage users' },
  { path: '/admin/settings', title: 'System settings' },
  { path: '/admin', title: 'Admin' },
];

/** The page's own name ("Battles"), or null when the route keeps the default title. */
export function getPageName(pathname: string): string | null {
  const hit = PAGE_TITLES.find(({ path }) => matchPath({ path, end: true }, pathname));
  return hit ? hit.title : null;
}

/** "Battles - Grim Dark Companion", or null when the route keeps the default title. */
export function getPageTitle(pathname: string): string | null {
  const name = getPageName(pathname);
  return name ? `${name} - ${SITE_NAME}` : null;
}

/**
 * Public Near Me pages set their own head (title, description, canonical, JSON-LD) from the data
 * they load, so the route-wide title must not overwrite it.
 */
export const managesOwnHead = (pathname: string): boolean => pathname === '/events' || pathname.startsWith('/events/') || pathname.startsWith('/venues/');
